import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useRegisterTopEscapeClose } from '../../hooks/useEscapeCloseStack';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { createPrivateLenderOffer, extractContractTermsFromPdf } from '../../services/privateLenderOfferService';
import { X, FileUp, Sparkles } from 'lucide-react';

/** Format digits with space as thousands separator, then " $" (e.g. "1234567" -> "1 234 567 $") */
function formatPrincipalAmountDisplay(digitsOrNumber) {
  const digits = typeof digitsOrNumber === 'number'
    ? String(Math.max(0, Math.floor(digitsOrNumber)))
    : String(digitsOrNumber ?? '').replace(/\D/g, '');
  if (!digits) return '';
  const withSpaces = digits.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  return `${withSpaces} $`;
}

/** Parse displayed value back to number (strips spaces and $) */
function parsePrincipalAmountDisplay(str) {
  if (!str || typeof str !== 'string') return NaN;
  const cleaned = str.replace(/\s/g, '').replace(/\$/g, '').replace(',', '.').trim();
  return cleaned === '' ? NaN : Number(cleaned);
}

/** Build a short French offer title from extracted terms when AI does not return offerTitle. */
function buildDefaultOfferTitle(extracted) {
  if (!extracted) return null;
  const parts = [];
  if (extracted.principalAmount != null && extracted.principalAmount > 0) {
    parts.push(`Prêt ${formatPrincipalAmountDisplay(extracted.principalAmount)}`);
  }
  if (extracted.interestRate != null) {
    parts.push(`à ${extracted.interestRate} %`);
  }
  if (extracted.termMonths != null) {
    parts.push(`${extracted.termMonths} mois`);
  }
  return parts.length > 0 ? parts.join(' - ') : null;
}

const CreateOfferModal = ({ property, onClose, onCreated }) => {
  const { currentUser } = useAuth();
  const { t } = useLanguage();
  const [comment, setComment] = useState('');
  const [principalAmount, setPrincipalAmount] = useState('');
  const [interestRate, setInterestRate] = useState('');
  const [termMonths, setTermMonths] = useState('');
  const [loanStartDate, setLoanStartDate] = useState('');
  const [loanMaturityDate, setLoanMaturityDate] = useState('');
  const [paymentFrequency, setPaymentFrequency] = useState('monthly');
  /** `daily` = prorata quotidien (365 j.) comme avant ; `monthly` = mensualité fixe P×taux/12 sauf 1er / dernier mois partiels. */
  const [interestCalculationMode, setInterestCalculationMode] = useState('daily');
  const [contractFile, setContractFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [error, setError] = useState('');
  const [conformitySuccess, setConformitySuccess] = useState(false);
  const [fieldErrors, setFieldErrors] = useState(() => new Set());
  const principalAmountInputRef = useRef(null);

  const getCurrentValuesForVerify = useCallback(() => {
    const amount = parsePrincipalAmountDisplay(principalAmount);
    const amountVal = !Number.isNaN(amount) ? amount : undefined;
    const rate = interestRate.trim() ? Number(String(interestRate).replace(/\s/g, '').replace(',', '.')) : undefined;
    const term = termMonths.trim() ? Number(termMonths) : undefined;
    return {
      ...(amountVal != null ? { principalAmount: amountVal } : {}),
      ...(rate != null && !Number.isNaN(rate) ? { interestRate: rate } : {}),
      ...(term != null && !Number.isNaN(term) ? { termMonths: term } : {}),
      ...(loanStartDate ? { loanStartDate } : {}),
      ...(loanMaturityDate ? { loanMaturityDate } : {}),
      ...(paymentFrequency ? { paymentFrequency } : {}),
    };
  }, [principalAmount, interestRate, termMonths, loanStartDate, loanMaturityDate, paymentFrequency]);

  // Keep cursor before " $" in Loan Amount so typing inserts in the right place
  useEffect(() => {
    if (principalAmount.endsWith(' $') && principalAmountInputRef.current && document.activeElement === principalAmountInputRef.current) {
      const pos = principalAmount.length - 2;
      requestAnimationFrame(() => {
        if (principalAmountInputRef.current) {
          principalAmountInputRef.current.setSelectionRange(pos, pos);
        }
      });
    }
  }, [principalAmount]);

  const handleFillOrVerifyWithAI = async () => {
    if (!contractFile) return;
    setExtracting(true);
    setError('');
    setConformitySuccess(false);
    setFieldErrors(new Set());
    try {
      const currentValues = getCurrentValuesForVerify();
      const { extracted, verification } = await extractContractTermsFromPdf(contractFile, Object.keys(currentValues).length ? currentValues : null);
      const errs = new Set();
      if (verification) {
        Object.entries(verification).forEach(([key, v]) => {
          if (v && v.match === false) errs.add(key);
        });
      }
      setFieldErrors(errs);
      if (errs.size) {
        setError(t('privateLenderOffer.documentDoesNotCorroborate'));
      } else if (verification && Object.keys(verification).length > 0) {
        setConformitySuccess(true);
      }
      if (extracted) {
        if (extracted.principalAmount != null && !principalAmount.trim()) setPrincipalAmount(formatPrincipalAmountDisplay(extracted.principalAmount));
        if (extracted.interestRate != null && !interestRate.trim()) setInterestRate(String(extracted.interestRate));
        if (extracted.termMonths != null && !termMonths.trim()) setTermMonths(String(extracted.termMonths));
        if (extracted.loanStartDate && !loanStartDate) setLoanStartDate(extracted.loanStartDate.slice(0, 10));
        if (extracted.loanMaturityDate && !loanMaturityDate) setLoanMaturityDate(extracted.loanMaturityDate.slice(0, 10));
        if (extracted.paymentFrequency && !paymentFrequency) setPaymentFrequency(extracted.paymentFrequency === 'quarterly' ? 'quarterly' : 'monthly');
        if (!comment.trim()) {
          const title = extracted.offerTitle || buildDefaultOfferTitle(extracted);
          if (title) setComment(title);
        }
      }
    } catch (err) {
      setError(err?.message || t('privateLenderOffer.summaryError'));
    } finally {
      setExtracting(false);
    }
  };

  const inputClass = (fieldKey) => {
    const base = 'w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500';
    return fieldErrors.has(fieldKey)
      ? `${base} border-red-500 bg-red-50`
      : `${base} border-gray-300`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!property?.id || !property?.uid || !currentUser?.uid) return;
    const amount = parsePrincipalAmountDisplay(principalAmount);
    const rate = Number(interestRate.replace(/\s/g, '').replace(',', '.'));
    const term = termMonths ? Number(termMonths) : undefined;
    if (Number.isNaN(amount) || !amount || Number.isNaN(rate) || !rate || !loanStartDate) {
      setError(t('error') || 'Please fill in loan amount, rate and start date.');
      return;
    }
    if (!contractFile) {
      setError(t('privateLenderOffer.contractRequired'));
      return;
    }
    setLoading(true);
    setError('');
    try {
      await createPrivateLenderOffer(
        {
          propertyId: property.id,
          ownerUid: property.uid,
          lenderUid: currentUser.uid,
          lenderDisplayName: currentUser.displayName || null,
          comment: comment.trim() || null,
          principalAmount: amount,
          interestRate: rate,
          termMonths: term ?? null,
loanStartDate: loanStartDate || null,
        loanMaturityDate: loanMaturityDate || null,
        paymentFrequency: paymentFrequency || null,
        interestPaymentDue: 'end_of_month',
        interestCalculationMode: interestCalculationMode === 'monthly' ? 'monthly' : 'daily',
      },
      contractFile
    );
      onCreated?.();
      onClose();
    } catch (err) {
      setError(err?.message || 'Error');
    } finally {
      setLoading(false);
    }
  };

  useRegisterTopEscapeClose(onClose);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="text-lg font-semibold">{t('privateLenderOffer.makeOffer')}</h3>
          <button type="button" onClick={onClose} className="p-1 rounded hover:bg-gray-100">
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div className="grid grid-cols-1 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('privateLenderOffer.principalAmount')} *
              </label>
              <input
                ref={principalAmountInputRef}
                type="text"
                inputMode="numeric"
                placeholder="0 $"
                value={principalAmount}
                onChange={(e) => {
                  setPrincipalAmount(formatPrincipalAmountDisplay(e.target.value));
                  setFieldErrors((s) => { const n = new Set(s); n.delete('principalAmount'); return n; });
                }}
                className={inputClass('principalAmount')}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('privateLenderOffer.interestRate')} *
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={interestRate}
                onChange={(e) => { setInterestRate(e.target.value); setFieldErrors((s) => { const n = new Set(s); n.delete('interestRate'); return n; }); }}
                className={inputClass('interestRate')}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('privateLenderOffer.termMonths')}
              </label>
              <input
                type="number"
                min="1"
                step="1"
                value={termMonths}
                onChange={(e) => { setTermMonths(e.target.value); setFieldErrors((s) => { const n = new Set(s); n.delete('termMonths'); return n; }); }}
                className={inputClass('termMonths')}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('privateLenderOffer.loanStartDate')} *
              </label>
              <input
                type="date"
                value={loanStartDate}
                onChange={(e) => { setLoanStartDate(e.target.value); setFieldErrors((s) => { const n = new Set(s); n.delete('loanStartDate'); return n; }); }}
                className={inputClass('loanStartDate')}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('privateLenderOffer.loanMaturityDate')}
              </label>
              <input
                type="date"
                value={loanMaturityDate}
                onChange={(e) => { setLoanMaturityDate(e.target.value); setFieldErrors((s) => { const n = new Set(s); n.delete('loanMaturityDate'); return n; }); }}
                className={inputClass('loanMaturityDate')}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('privateLenderOffer.paymentFrequency')}
              </label>
              <select
                value={paymentFrequency}
                onChange={(e) => { setPaymentFrequency(e.target.value); setFieldErrors((s) => { const n = new Set(s); n.delete('paymentFrequency'); return n; }); }}
                className={inputClass('paymentFrequency')}
              >
                <option value="monthly">{t('privateLenderOffer.paymentFrequency_monthly')}</option>
                <option value="quarterly">{t('privateLenderOffer.paymentFrequency_quarterly')}</option>
              </select>
            </div>
            <div className="col-span-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('privateLenderOffer.interestCalculationMode')}
              </label>
              <select
                value={interestCalculationMode}
                onChange={(e) => setInterestCalculationMode(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="daily">{t('privateLenderOffer.interestCalculationMode_daily')}</option>
                <option value="monthly">{t('privateLenderOffer.interestCalculationMode_monthly')}</option>
              </select>
              <p className="text-xs text-gray-500 mt-1.5 leading-relaxed">
                {interestCalculationMode === 'monthly'
                  ? t('privateLenderOffer.interestCalculationMode_monthlyHint')
                  : t('privateLenderOffer.interestCalculationMode_dailyHint')}
              </p>
              <p className="text-xs text-gray-500 mt-1">{t('privateLenderOffer.interestPaymentDueStandardNote')}</p>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('privateLenderOffer.comment')}
            </label>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder={t('privateLenderOffer.commentPlaceholder')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              rows={3}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('privateLenderOffer.contractFile')} *
            </label>
            <label className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 hover:bg-gray-100 cursor-pointer text-sm">
              <FileUp className="w-4 h-4" />
              {contractFile ? contractFile.name : t('privateLenderOffer.chooseContract')}
              <input
                type="file"
                accept=".pdf"
                className="hidden"
                onChange={(e) => {
                setContractFile(e.target.files?.[0] || null);
                setConformitySuccess(false);
              }}
              />
            </label>
            {contractFile && (
              <button
                type="button"
                disabled={extracting}
                onClick={handleFillOrVerifyWithAI}
                className="mt-3 inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-lg hover:bg-indigo-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Sparkles className="w-4 h-4" />
                {extracting ? t('privateLenderOffer.extracting') : t('privateLenderOffer.fillOrVerifyWithAI')}
              </button>
            )}
          </div>
          {error && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">
              {error}
            </p>
          )}
          {conformitySuccess && (
            <p className="text-sm text-green-800 bg-green-50 border border-green-200 rounded px-3 py-2">
              {t('privateLenderOffer.conformitySuccess')}
            </p>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 border rounded-lg hover:bg-gray-50">
              {t('cancel')}
            </button>
            <button
              type="submit"
              disabled={loading || !contractFile}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? t('privateLenderOffer.submitting') : t('privateLenderOffer.submit')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateOfferModal;
